/**
 * Wann darf die Punktliste über die Datenbank klagen — und mit welchem Satz?
 *
 * ## Warum es das gibt
 *
 * Nutzer-Befund 12.09.2026: Eine Sitzung stand über Nacht offen. Am Morgen
 * standen DREI Meldungen auf dem Bildschirm, ohne dass jemand etwas angefasst
 * hatte — der orange Sitzungs-Balken oben („Deine Sitzung ist abgelaufen"),
 * dazu ein roter Kasten „Fataler Fehler! … (JSON.parse: unexpected end of data
 * at line 1 column 1 of the JSON data)" und ein oranger „Keine Datenbank! … die
 * App startet ohne Daten". Beide Kästen waren auf „bleibt stehen" gesetzt.
 *
 * Es war EINE Sache, dreimal gesagt. Ohne gültigen Schlüssel weist der Server
 * ab, die Antwort ist leer, und die App übersetzte die leere Antwort in einen
 * fatalen Fehler. Der Balken oben sagte die Ursache bereits — samt Knopf, mit
 * dem man etwas dagegen tun kann. Die beiden Kästen sagten nichts dazu, was der
 * Balken nicht schon sagte, und der eine tat es in einer Sprache, die niemandem
 * hilft.
 *
 * Derselbe orange Kasten kam **bei jedem Start**, bevor die Anmeldung durch war
 * (zweiter Nutzer-Befund am selben Tag). Dort war er nicht nur laut, sondern
 * falsch: Die App startete sehr wohl mit Daten, sie waren eine Sekunde später da.
 *
 * ## Die Regel
 *
 * Eine Meldung braucht einen Adressaten — jemanden, der etwas dagegen tun kann,
 * und eine Aussage, die er nicht schon woanders liest. Solange die Anmeldung
 * nicht trägt, ist eine fehlende Datenbank keine Aussage über die Datenbank.
 */

/** Woran die Punktliste ablesen kann, ob eine Meldung etwas wert ist. */
export interface DbMeldungsLage {
	/**
	 * Steht der Sitzungs-Balken schon?
	 *
	 * Dann ist die Sache gesagt, in besseren Worten und mit einem Knopf dazu.
	 */
	balkenSteht: boolean;
	/**
	 * Fehlt der Anmelde-Schlüssel oder ist er abgelaufen?
	 *
	 * Kommt aus `anmeldungFehlt()` der Bibliothek — derselbe Blick, mit dem die
	 * Wache den Balken aufstellt. Er deckt beide Fälle ab: den Start, an dem noch
	 * nie einer da war, und die Nacht, nach der keiner mehr gilt.
	 */
	anmeldungFehlt: boolean;
	/**
	 * Sieht das **Gerät** ein Netz?
	 *
	 * Ohne Netz ist eine fehlende Verbindung zum Server der Normalfall und kein
	 * Ereignis; niemand kann etwas dagegen tun. Dieselbe Zurückhaltung wie bei
	 * `anmeldungFehlt()` in der Bibliothek: lieber keine Warnung als eine, die
	 * man nach zwei Tagen übersieht.
	 *
	 * **Der Offline-Modus der App zählt hier NICHT als „ohne Netz"** — er ist
	 * eine Entscheidung über den Abgleich, keine Aussage über das Netz. Er wird
	 * hier gar nicht gefragt: Offline liegt eine lokale Datenbank vor, und wenn
	 * die fehlt, ist das sehr wohl eine Nachricht.
	 */
	geraetOnline: boolean;
}

/**
 * Soll eine Meldung über die Datenbank gezeigt werden?
 *
 * `false` heißt nicht „alles in Ordnung", sondern: Diese Meldung sagt nichts,
 * was der Nutzer nicht schon weiß oder nicht ändern kann. In der Konsole steht
 * es weiterhin.
 */
export function datenbankMeldungZeigen(lage: DbMeldungsLage): boolean {
	if (lage.balkenSteht) return false;
	if (lage.anmeldungFehlt) return false;
	if (!lage.geraetOnline) return false;
	return true;
}

/**
 * Der Fehler in Worten, die jemand ohne Entwicklerkonsole versteht.
 *
 * Vorher stand der rohe `message` im Kasten. Bei einer abgewiesenen Anfrage ist
 * das „JSON.parse: unexpected end of data at line 1 column 1 of the JSON data" —
 * eine wahre Aussage über eine leere Antwort, aus der niemand ableiten kann, was
 * zu tun ist.
 *
 * **Die technische Fassung bleibt in der Konsole.** Sie wird hier nicht ersetzt,
 * sondern übersetzt; wer sucht, findet sie weiter.
 */
export function dbFehlerText(fehler: unknown): string {
	const f = fehler as { status?: number; name?: string; message?: string } | null;
	const status = Number(f?.status);

	// 405 kommt vom oeffentlichen Nur-Lese-Weg des Gateways: Ohne tragende
	// Anmeldung wird die Verbindung dorthin aufgebaut, und dort ist nur GET
	// erlaubt. Eine Sammelabfrage (PouchDB schickt sie als POST) bekommt dann
	// „method_not_allowed_on_public" - die Abhilfe ist dieselbe wie bei 401
	// (Nutzer-Befund 24.09.2026, Druckansicht).
	if (status === 401 || status === 403 || status === 405) {
		return 'Der Server hat die Anfrage abgewiesen. Bitte neu anmelden.';
	}
	if (status >= 500 && status < 600) {
		return 'Der Server hat einen Fehler gemeldet. Bitte es später noch einmal versuchen.';
	}
	// Eine leere Antwort kommt beim Parsen heraus, nicht als HTTP-Fehler: Der
	// Server hat abgewiesen oder gar nicht geantwortet, und der Versuch, nichts
	// als JSON zu lesen, bricht in der ersten Spalte ab. Genau dieser Satz stand
	// dem Nutzer am 12.09.2026 im Kasten.
	if (f?.name === 'SyntaxError' || /JSON/i.test(f?.message ?? '')) {
		return 'Der Server hat keine verwertbare Antwort geschickt.';
	}
	const roh = (f?.message ?? '').trim();
	return roh ? roh : 'Die Verbindung zur Datenbank ist fehlgeschlagen.';
}
